// secureFiles.js — short-lived links for private documents (owner, 10 Oct 2026).
//
// A stored document link carries a key that never expires. Screens no longer show that link:
// they ask the server (getFileLinks) for one that works for 10 minutes — the server checks who
// is asking and records it. Many pictures on one screen are asked for together in ONE request,
// and a link is reused until shortly before it dies.
//
//   isPrivateFile(value)        is this one of our private documents? (URL or bare path)
//   secureLink(value)           → Promise<string>  a fresh link (or the value itself when it is
//                                 not a private document — product photos, logos, other sites)
//
// Pure helpers are exported for tests; the network call is injected.
import { getFunctions, httpsCallable } from 'firebase/functions';

const BUCKET = 'al-sami-electronics-doha';
const PRIVATE_FOLDERS = ['instantApplications/', 'customerApplications/', 'customerDocuments/', 'hangamaApplications/', 'collectionVisits/', 'cashAttachments/', 'bankDeposits/', 'bankTransfers/', 'safeCopies/'];
const REUSE_MS = 8 * 60 * 1000; // links live 10 minutes; ask again after 8
const BATCH_MS = 40;
const BATCH_MAX = 60;

// 'https://firebasestorage.googleapis.com/v0/b/<bucket>/o/<path>?…' or a bare path → the path.
export function pathOfFile(value) {
  const s = String(value || '').trim();
  if (!s) return null;
  const m = s.match(/^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?#]+)/);
  if (m) { if (m[1] !== BUCKET) return null; try { return decodeURIComponent(m[2]); } catch (_) { return null; } }
  if (/^[a-z]+:/i.test(s) || s.startsWith('/')) return null; // another site, a blob:, a data: or a page path
  return s;
}
export function isPrivateFile(value) {
  const p = pathOfFile(value);
  return !!p && PRIVATE_FOLDERS.some((f) => p.startsWith(f));
}

const cache = new Map(); // value → { url, at }
let waiting = []; // [{ value, resolve }]
let timer = null;
let fetchLinks = (files) => httpsCallable(getFunctions(undefined, 'me-central1'), 'getFileLinks')({ files }).then((r) => (r.data && r.data.links) || {});
/** Tests swap the network call. */
export function _setFetchLinks(fn) { fetchLinks = fn; cache.clear(); waiting = []; if (timer) { clearTimeout(timer); timer = null; } }

async function flush() {
  timer = null;
  const batch = waiting; waiting = [];
  const values = [...new Set(batch.map((w) => w.value))];
  for (let i = 0; i < values.length; i += BATCH_MAX) {
    const part = values.slice(i, i + BATCH_MAX);
    let links = {};
    try { links = await fetchLinks(part); } catch (_) { links = {}; }
    const now = Date.now();
    part.forEach((v) => { if (links[v]) cache.set(v, { url: links[v], at: now }); });
    // Not allowed / failed: fall back to the stored value, so a screen never goes blank while the
    // old links still exist (stage 4 switches them off).
    batch.filter((w) => part.includes(w.value)).forEach((w) => w.resolve(links[w.value] || w.value));
  }
}

export function secureLink(value) {
  if (!isPrivateFile(value)) return Promise.resolve(value);
  const hit = cache.get(value);
  if (hit && Date.now() - hit.at < REUSE_MS) return Promise.resolve(hit.url);
  return new Promise((resolve) => {
    waiting.push({ value, resolve });
    if (!timer) timer = setTimeout(flush, BATCH_MS);
  });
}
